import { ArrowUpDown, ArrowUp, ArrowDown, SearchX } from "lucide-react";
import { fmtNum } from "@/lib/seo";

const COLUMNS = [
  { key: "domain", label: "Domain" },
  { key: "as", label: "AS" },
  { key: "da", label: "DA" },
  { key: "pa", label: "PA" },
  { key: "ss", label: "SS" },
  { key: "backlinks", label: "Backlinks" },
  { key: "quality_bl", label: "Quality BL" },
  { key: "ref_domains", label: "Ref. Domains" },
  { key: "df_ref_domains", label: "DF Ref. Dom." },
  { key: "nofollow", label: "Nofollow" },
  { key: "lp", label: "LP" },
  { key: "outbound", label: "Outbound" },
  { key: "status", label: "Status" },
  { key: "last_crawl", label: "Last Crawl" },
];

const StatusBadge = ({ status }) => {
  const styles = {
    OK: "bg-green-100 text-green-800",
    Redirect: "bg-amber-100 text-amber-800",
    Error: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.Error}`}>
      {status}
    </span>
  );
};

const daColor = (v) => {
  if (v === null || v === undefined) return "text-gray-400";
  if (v >= 60) return "text-green-700 font-semibold";
  if (v >= 30) return "text-amber-700 font-semibold";
  return "text-gray-700";
};

export const ReportTable = ({ rows, loading, sortKey, sortDir, onSort, hasScanned }) => {
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} className="opacity-40 group-hover:opacity-100 transition-opacity" />;
    return sortDir === "asc" ? <ArrowUp size={12} className="text-[#C0392B]" /> : <ArrowDown size={12} className="text-[#C0392B]" />;
  };

  return (
    <div className="w-full overflow-x-auto" data-testid="report-table-container">
      <table className="w-full text-left border-collapse min-w-[1300px]" data-testid="report-table">
        <thead>
          <tr>
            <th className="px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">#</th>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                data-testid={`th-${c.key}`}
                onClick={() => onSort(c.key)}
                className="px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors group whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{c.label} <SortIcon col={c.key} /></span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 &&
            Array.from({ length: 4 }).map((_, i) => (
              <tr key={`sk-${i}`} data-testid="skeleton-row">
                {Array.from({ length: 14 }).map((__, j) => (
                  <td key={j} className="px-4 py-3.5 border-b border-gray-100">
                    <div className="animate-pulse bg-gray-200 rounded h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={15} className="px-4 py-20 text-center" data-testid="empty-state">
                <SearchX size={40} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">
                  {hasScanned ? "No results match your current filters." : "No scan has been run yet."}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {hasScanned ? "Adjust or reset your filters to see results." : "Paste your domains above and hit \u201CCheck DA PA\u201D to generate a report."}
                </p>
              </td>
            </tr>
          )}
          {rows.length > 0 &&
            rows.map((r, i) => (
              <tr key={`${r.domain}-${i}`} data-testid={`report-row-${i}`} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-4 py-3 text-sm text-gray-400 border-b border-gray-100">{i + 1}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-b border-gray-100">{r.domain}</td>
                <td className={`px-4 py-3 text-sm border-b border-gray-100 ${daColor(r.as)}`}>{r.as != null ? r.as : "—"}</td>
                <td className={`px-4 py-3 text-sm border-b border-gray-100 ${daColor(r.da)}`}>{r.da ?? "—"}</td>
                <td className={`px-4 py-3 text-sm border-b border-gray-100 ${daColor(r.pa)}`}>{r.pa ?? "—"}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{r.ss ?? "—"}{r.ss !== null && r.ss !== undefined ? "%" : ""}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.backlinks)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.quality_bl)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.ref_domains)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.df_ref_domains)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.nofollow)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{r.lp ?? "—"}</td>
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{fmtNum(r.outbound)}</td>
                <td className="px-4 py-3 border-b border-gray-100"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-sm text-gray-500 border-b border-gray-100 whitespace-nowrap">{r.last_crawl ?? "—"}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
